import React, { useEffect, useState } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { getApiBaseUrl } from '../../config/runtime';
import {
  Box,
  Paper,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  CircularProgress,
  Alert,
  Chip,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  FormControlLabel,
  Checkbox,
  IconButton,
  Card,
  CardContent,
  CardActions,
  Stack,
  useTheme,
  useMediaQuery,
  FormGroup,
  Autocomplete,
  MenuItem,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import SmartToyIcon from '@mui/icons-material/SmartToy';

interface SubscriptionType {
  id: string;
  code: string;
  name: string;
  description: string | null;
  price: number;
  durationInDays: number | null;
  isTrial: boolean;
  planFamily: string;
  includesHardwareRental: boolean;
  trapDeviceTypeId: string | null;
  includesApiAccess: boolean;
  includesDataApi: boolean;
  stripePriceId: string | null;
  dateCreated: string;
  archivedDate: string | null;
}

interface PlanForm {
  code: string;
  name: string;
  description: string;
  price: string;
  durationInDays: string;
  isTrial: boolean;
  planFamily: string;
  includesHardwareRental: boolean;
  trapDeviceTypeId: string;
  includesApiAccess: boolean;
  includesDataApi: boolean;
  stripePriceId: string;
}

interface DeviceTypeOption {
  id: string;
  name: string;
}

interface InferenceModelOption {
  id: string;
  name: string;
  displayName: string;
}

const emptyForm: PlanForm = {
  code: '',
  name: '',
  description: '',
  price: '0',
  durationInDays: '',
  isTrial: false,
  planFamily: '',
  includesHardwareRental: false,
  trapDeviceTypeId: '',
  includesApiAccess: false,
  includesDataApi: false,
  stripePriceId: '',
};

const SubscriptionPlansManagement: React.FC = () => {
  const auth = useAuth();
  const apiUrl = getApiBaseUrl();
  const authHeader = { Authorization: `Bearer ${auth.tokens?.accessToken}` };
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  const [plans, setPlans] = useState<SubscriptionType[]>([]);
  const [includeArchived, setIncludeArchived] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [form, setForm] = useState<PlanForm>(emptyForm);
  const [saving, setSaving] = useState(false);
  const [formError, setFormError] = useState<string | null>(null);
  const [deviceTypes, setDeviceTypes] = useState<DeviceTypeOption[]>([]);

  const [allModels, setAllModels] = useState<InferenceModelOption[]>([]);
  const [modelsDialogOpen, setModelsDialogOpen] = useState(false);
  const [modelsDialogPlan, setModelsDialogPlan] = useState<SubscriptionType | null>(null);
  const [selectedModelIds, setSelectedModelIds] = useState<Set<string>>(new Set());
  const [modelsLoading, setModelsLoading] = useState(false);
  const [modelsSaving, setModelsSaving] = useState(false);
  const [modelsError, setModelsError] = useState<string | null>(null);

  const fetchPlans = async () => {
    setLoading(true);
    try {
      const response = await fetch(`${apiUrl}/api/subscription-types?includeArchived=${includeArchived}`, {
        headers: authHeader,
      });
      if (!response.ok) throw new Error('Failed to fetch subscription plans');
      const data = await response.json();
      setPlans(Array.isArray(data) ? data : []);
      setError(null);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'An error occurred');
    } finally {
      setLoading(false);
    }
  };

  const fetchDeviceTypes = async () => {
    if (deviceTypes.length > 0) return;
    try {
      const response = await fetch(`${apiUrl}/api/device-types`, { headers: authHeader });
      if (!response.ok) throw new Error('Failed to fetch device types');
      const data = await response.json();
      setDeviceTypes(Array.isArray(data) ? data : []);
    } catch (err) {
      setFormError(err instanceof Error ? err.message : 'An error occurred');
    }
  };

  // Existing families, offered as suggestions so a new tier joins its siblings
  // by picking the exact same string rather than retyping it.
  const planFamilies = Array.from(new Set(plans.map((p) => p.planFamily).filter(Boolean))).sort();

  useEffect(() => {
    if (auth.isSignedIn) fetchPlans();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [auth.isSignedIn, includeArchived]);

  const openCreateDialog = () => {
    setEditingId(null);
    setForm(emptyForm);
    setFormError(null);
    setDialogOpen(true);
    fetchDeviceTypes();
  };

  const openEditDialog = (plan: SubscriptionType) => {
    setEditingId(plan.id);
    setForm({
      code: plan.code,
      name: plan.name,
      description: plan.description ?? '',
      price: String(plan.price),
      durationInDays: plan.durationInDays != null ? String(plan.durationInDays) : '',
      isTrial: plan.isTrial,
      planFamily: plan.planFamily ?? '',
      includesHardwareRental: plan.includesHardwareRental,
      trapDeviceTypeId: plan.trapDeviceTypeId ?? '',
      includesApiAccess: plan.includesApiAccess,
      includesDataApi: plan.includesDataApi,
      stripePriceId: plan.stripePriceId ?? '',
    });
    setFormError(null);
    setDialogOpen(true);
    fetchDeviceTypes();
  };

  const handleSave = async () => {
    setSaving(true);
    setFormError(null);
    try {
      const body: Record<string, unknown> = {
        name: form.name,
        description: form.description || undefined,
        price: parseFloat(form.price) || 0,
        durationInDays: form.durationInDays ? parseInt(form.durationInDays, 10) : undefined,
        isTrial: form.isTrial,
        planFamily: form.planFamily.trim(),
        includesHardwareRental: form.includesHardwareRental,
        trapDeviceTypeId: form.includesHardwareRental && form.trapDeviceTypeId ? form.trapDeviceTypeId : null,
        includesApiAccess: form.includesApiAccess,
        includesDataApi: form.includesDataApi,
        stripePriceId: form.stripePriceId.trim() || null,
      };
      let response: Response;
      if (editingId) {
        response = await fetch(`${apiUrl}/api/subscription-types/${editingId}`, {
          method: 'PUT',
          headers: { ...authHeader, 'Content-Type': 'application/json' },
          body: JSON.stringify(body),
        });
      } else {
        response = await fetch(`${apiUrl}/api/subscription-types`, {
          method: 'POST',
          headers: { ...authHeader, 'Content-Type': 'application/json' },
          body: JSON.stringify({ ...body, code: form.code }),
        });
      }
      if (!response.ok) {
        const data = await response.json().catch(() => ({}));
        throw new Error(data.error || 'Failed to save subscription plan');
      }
      setDialogOpen(false);
      await fetchPlans();
    } catch (err) {
      setFormError(err instanceof Error ? err.message : 'An error occurred');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (plan: SubscriptionType) => {
    if (!window.confirm(`Archive the "${plan.name}" plan? Businesses already on it are unaffected.`)) return;
    try {
      const response = await fetch(`${apiUrl}/api/subscription-types/${plan.id}`, {
        method: 'DELETE',
        headers: authHeader,
      });
      if (!response.ok && response.status !== 204) {
        throw new Error('Failed to archive plan');
      }
      await fetchPlans();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'An error occurred');
    }
  };

  const openModelsDialog = async (plan: SubscriptionType) => {
    setModelsDialogPlan(plan);
    setModelsError(null);
    setModelsDialogOpen(true);
    setModelsLoading(true);
    try {
      const [modelsRes, allowedRes] = await Promise.all([
        allModels.length > 0
          ? Promise.resolve(null)
          : fetch(`${apiUrl}/api/inference-models?includeInactive=true`, { headers: authHeader }),
        fetch(`${apiUrl}/api/subscription-types/${plan.id}/models`, { headers: authHeader }),
      ]);
      if (modelsRes) {
        if (!modelsRes.ok) throw new Error('Failed to fetch inference models');
        const data = await modelsRes.json();
        setAllModels(Array.isArray(data) ? data : []);
      }
      if (!allowedRes.ok) throw new Error('Failed to fetch allowed models for this plan');
      const allowedIds: string[] = await allowedRes.json();
      setSelectedModelIds(new Set(allowedIds));
    } catch (err) {
      setModelsError(err instanceof Error ? err.message : 'An error occurred');
    } finally {
      setModelsLoading(false);
    }
  };

  const toggleModel = (modelId: string) => {
    setSelectedModelIds((prev) => {
      const next = new Set(prev);
      if (next.has(modelId)) next.delete(modelId);
      else next.add(modelId);
      return next;
    });
  };

  const handleSaveModels = async () => {
    if (!modelsDialogPlan) return;
    setModelsSaving(true);
    setModelsError(null);
    try {
      const response = await fetch(`${apiUrl}/api/subscription-types/${modelsDialogPlan.id}/models`, {
        method: 'PUT',
        headers: { ...authHeader, 'Content-Type': 'application/json' },
        body: JSON.stringify({ inferenceModelIds: Array.from(selectedModelIds) }),
      });
      if (!response.ok && response.status !== 204) {
        const data = await response.json().catch(() => ({}));
        throw new Error(data.error || 'Failed to save allowed models');
      }
      setModelsDialogOpen(false);
    } catch (err) {
      setModelsError(err instanceof Error ? err.message : 'An error occurred');
    } finally {
      setModelsSaving(false);
    }
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h6">Subscription Plans</Typography>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          <FormControlLabel
            control={<Checkbox checked={includeArchived} onChange={(e) => setIncludeArchived(e.target.checked)} />}
            label="Show archived"
          />
          <Button variant="contained" startIcon={<AddIcon />} onClick={openCreateDialog}>
            New Plan
          </Button>
        </Box>
      </Box>

      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

      {isMobile ? (
        <Stack spacing={1.5}>
          {plans.map((plan) => (
            <Card key={plan.id} variant="outlined">
              <CardContent sx={{ pb: 1 }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 0.5 }}>
                  <Typography variant="subtitle2" fontWeight={600}>{plan.name}</Typography>
                  {plan.archivedDate ? (
                    <Chip label="Archived" size="small" color="default" />
                  ) : (
                    <Chip label="Active" size="small" color="success" />
                  )}
                </Box>
                <Typography variant="caption" color="text.secondary" sx={{ fontFamily: 'monospace', display: 'block' }}>
                  {plan.code}{plan.planFamily ? ` · ${plan.planFamily}` : ''}
                </Typography>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 1 }}>
                  <Typography variant="body2">{plan.price === 0 ? 'Free' : `$${plan.price.toFixed(2)}`}</Typography>
                  <Typography variant="body2" color="text.secondary">
                    {plan.durationInDays ? `${plan.durationInDays} days` : 'Rolling'}
                  </Typography>
                  {plan.isTrial && <Chip label="Trial" size="small" color="info" />}
                </Box>
              </CardContent>
              <CardActions>
                <Button size="small" startIcon={<EditIcon fontSize="small" />} onClick={() => openEditDialog(plan)}>
                  Edit
                </Button>
                <Button size="small" startIcon={<SmartToyIcon fontSize="small" />} onClick={() => openModelsDialog(plan)}>
                  Models
                </Button>
                {!plan.archivedDate && (
                  <Button size="small" color="error" startIcon={<DeleteIcon fontSize="small" />} onClick={() => handleDelete(plan)}>
                    Archive
                  </Button>
                )}
              </CardActions>
            </Card>
          ))}
        </Stack>
      ) : (
        <TableContainer component={Paper}>
          <Table sx={{ '& .MuiTableCell-root': { py: 1 } }}>
            <TableHead>
              <TableRow>
                <TableCell>Code</TableCell>
                <TableCell>Name</TableCell>
                <TableCell>Family</TableCell>
                <TableCell>Price</TableCell>
                <TableCell>Duration</TableCell>
                <TableCell>Trial</TableCell>
                <TableCell>Status</TableCell>
                <TableCell align="right">Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {plans.map((plan) => (
                <TableRow key={plan.id}>
                  <TableCell sx={{ fontFamily: 'monospace' }}>{plan.code}</TableCell>
                  <TableCell>{plan.name}</TableCell>
                  <TableCell sx={{ fontFamily: 'monospace' }}>{plan.planFamily || '—'}</TableCell>
                  <TableCell>{plan.price === 0 ? 'Free' : `$${plan.price.toFixed(2)}`}</TableCell>
                  <TableCell>{plan.durationInDays ? `${plan.durationInDays} days` : 'Rolling'}</TableCell>
                  <TableCell>{plan.isTrial ? <Chip label="Trial" size="small" color="info" /> : '—'}</TableCell>
                  <TableCell>
                    {plan.archivedDate ? (
                      <Chip label="Archived" size="small" color="default" />
                    ) : (
                      <Chip label="Active" size="small" color="success" />
                    )}
                  </TableCell>
                  <TableCell align="right">
                    <IconButton size="small" onClick={() => openEditDialog(plan)} title="Edit plan">
                      <EditIcon fontSize="small" />
                    </IconButton>
                    <IconButton size="small" onClick={() => openModelsDialog(plan)} title="Manage allowed models">
                      <SmartToyIcon fontSize="small" />
                    </IconButton>
                    {!plan.archivedDate && (
                      <IconButton size="small" color="error" onClick={() => handleDelete(plan)}>
                        <DeleteIcon fontSize="small" />
                      </IconButton>
                    )}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      <Dialog open={dialogOpen} onClose={() => setDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>{editingId ? 'Edit Plan' : 'New Plan'}</DialogTitle>
        <DialogContent>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 1 }}>
            {formError && <Alert severity="error">{formError}</Alert>}
            <TextField
              label="Code"
              value={form.code}
              onChange={(e) => setForm({ ...form, code: e.target.value.toUpperCase() })}
              disabled={!!editingId}
              helperText={editingId ? 'Code cannot be changed after creation' : 'e.g. STANDARD'}
              fullWidth
            />
            <TextField
              label="Name"
              value={form.name}
              onChange={(e) => setForm({ ...form, name: e.target.value })}
              fullWidth
            />
            <TextField
              label="Description"
              value={form.description}
              onChange={(e) => setForm({ ...form, description: e.target.value })}
              multiline
              minRows={2}
              fullWidth
            />
            <TextField
              label="Price"
              type="number"
              value={form.price}
              onChange={(e) => setForm({ ...form, price: e.target.value })}
              inputProps={{ step: '0.01', min: 0 }}
              fullWidth
            />
            <TextField
              label="Duration (days)"
              type="number"
              value={form.durationInDays}
              onChange={(e) => setForm({ ...form, durationInDays: e.target.value })}
              helperText="Leave blank for rolling/non-expiring"
              inputProps={{ min: 1 }}
              fullWidth
            />
            <FormControlLabel
              control={
                <Checkbox
                  checked={form.isTrial}
                  onChange={(e) => setForm({ ...form, isTrial: e.target.checked })}
                />
              }
              label="Is Trial Plan"
            />
            <Autocomplete
              freeSolo
              options={planFamilies}
              value={form.planFamily}
              onInputChange={(_, value) => setForm((f) => ({ ...f, planFamily: value.toUpperCase() }))}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Plan Family"
                  required
                  helperText="Groups this plan with its Trial/Paid siblings — one active subscription per family per business"
                  inputProps={{ ...params.inputProps, maxLength: 50 }}
                />
              )}
            />
            <FormControlLabel
              control={
                <Checkbox
                  checked={form.includesHardwareRental}
                  onChange={(e) => setForm({ ...form, includesHardwareRental: e.target.checked })}
                />
              }
              label="Includes Hardware Rental"
            />
            {form.includesHardwareRental && (
              <TextField
                select
                label="Trap Device Type"
                value={form.trapDeviceTypeId}
                onChange={(e) => setForm({ ...form, trapDeviceTypeId: e.target.value })}
                helperText="Which device type this plan's rental provisions"
                fullWidth
              >
                <MenuItem value=""><em>None</em></MenuItem>
                {deviceTypes.map((dt) => (
                  <MenuItem key={dt.id} value={dt.id}>{dt.name}</MenuItem>
                ))}
              </TextField>
            )}
            <FormControlLabel
              control={
                <Checkbox
                  checked={form.includesApiAccess}
                  onChange={(e) => setForm({ ...form, includesApiAccess: e.target.checked })}
                />
              }
              label="Includes API Access"
            />
            <FormControlLabel
              control={
                <Checkbox
                  checked={form.includesDataApi}
                  onChange={(e) => setForm({ ...form, includesDataApi: e.target.checked })}
                />
              }
              label="Includes Data API (container and plate sightings)"
            />
            <TextField
              label="Stripe Price ID"
              value={form.stripePriceId}
              onChange={(e) => setForm({ ...form, stripePriceId: e.target.value })}
              helperText="e.g. price_1Abc… — leave blank if not sold through Stripe"
              inputProps={{ maxLength: 255 }}
              fullWidth
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDialogOpen(false)}>Cancel</Button>
          <Button
            variant="contained"
            onClick={handleSave}
            disabled={saving || !form.name || !form.planFamily.trim() || (!editingId && !form.code)}
          >
            {saving ? 'Saving…' : 'Save'}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={modelsDialogOpen} onClose={() => setModelsDialogOpen(false)} maxWidth="xs" fullWidth>
        <DialogTitle>
          Allowed Models — {modelsDialogPlan?.name}
        </DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
            Models a business on this plan can assign to their devices. Leave none selected to leave this plan
            unrestricted (all active models assignable).
          </Typography>
          {modelsError && <Alert severity="error" sx={{ mb: 1 }}>{modelsError}</Alert>}
          {modelsLoading ? (
            <Box display="flex" justifyContent="center" py={3}>
              <CircularProgress size={28} />
            </Box>
          ) : (
            <FormGroup>
              {allModels.map((m) => (
                <FormControlLabel
                  key={m.id}
                  control={
                    <Checkbox
                      checked={selectedModelIds.has(m.id)}
                      onChange={() => toggleModel(m.id)}
                    />
                  }
                  label={m.displayName || m.name}
                />
              ))}
              {allModels.length === 0 && (
                <Typography variant="body2" color="text.secondary">No inference models exist yet.</Typography>
              )}
            </FormGroup>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setModelsDialogOpen(false)}>Cancel</Button>
          <Button variant="contained" onClick={handleSaveModels} disabled={modelsSaving || modelsLoading}>
            {modelsSaving ? 'Saving…' : 'Save'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default SubscriptionPlansManagement;
